import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  getAdminV1StoresStoreIdExports,
  getAdminV1StoresStoreIdExportsId,
  postAdminV1StoresStoreIdExports,
  type ExportJob,
  type GetAdminV1StoresStoreIdExports200,
  type PostAdminV1StoresStoreIdExportsBody,
} from '@nucleus-commerce/api-client/admin';
import {
  ApiError,
  getHttp,
  isApiErrorBody,
  kindFromStatus,
} from '@nucleus-commerce/api-client/transport';

import { filenameFromDisposition } from '../schemas/job-rules';

export interface ExportFile {
  /** From Content-Disposition; null when the header is missing or hidden by CORS. */
  filename: string | null;
  blob: Blob;
}

/**
 * The download is read as text so the CSV arrives untouched. Axios does not parse error bodies of
 * a text response, so a 409/404 envelope is parsed here to keep its code and meta.
 */
export function withEnvelope(thrown: unknown): ApiError {
  const error = ApiError.from(thrown);
  if (error.status === undefined) return error;
  const data = (error.cause as { response?: { data?: unknown } } | undefined)?.response?.data;
  if (typeof data !== 'string') return error;
  let body: unknown;
  try {
    body = JSON.parse(data);
  } catch {
    return error;
  }
  if (!isApiErrorBody(body)) return error;
  return new ApiError({
    kind: kindFromStatus(error.status),
    code: body.error.code,
    message: body.error.message,
    status: error.status,
    requestId: body.error.requestId || error.requestId,
    details: body.error.details,
    meta: body.error.meta,
    cause: error,
  });
}

export const exportsApi = {
  list: (storeId: string, params: ListParams): Promise<GetAdminV1StoresStoreIdExports200> =>
    getAdminV1StoresStoreIdExports({ storeId }, params),
  get: (storeId: string, id: string): Promise<ExportJob> =>
    getAdminV1StoresStoreIdExportsId({ storeId, id }),
  create: (storeId: string, body: PostAdminV1StoresStoreIdExportsBody): Promise<ExportJob> =>
    postAdminV1StoresStoreIdExports({ storeId }, body),
  /** Through the API client so the cookie session and its client header go along (ADR 0004). */
  download: async (storeId: string, id: string): Promise<ExportFile> => {
    try {
      const response = await getHttp().get<string>(
        `/admin/v1/stores/${storeId}/exports/${id}/download`,
        { responseType: 'text', headers: { Accept: 'text/csv, application/json' } },
      );
      const disposition: unknown = response.headers['content-disposition'];
      return {
        filename: filenameFromDisposition(typeof disposition === 'string' ? disposition : null),
        blob: new Blob([response.data], { type: 'text/csv;charset=utf-8' }),
      };
    } catch (error) {
      throw withEnvelope(error);
    }
  },
};
